import React, { FC, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useReservationStore } from "hooks";
import { CheckCircle2, Mail } from "lucide-react";
import { WhatsAppButton, WHATSAPP_PHONE_DISPLAY } from "components/WhatsAppContact/WhatsAppContact";
import Seo from "components/Seo/Seo";
import { trackEvent } from "utils/analytics";

export interface PayPageProps {
  className?: string;
}

const btnGradient =
  "inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 py-3 text-base font-bold uppercase tracking-wide text-white shadow-lg shadow-violet-600/30 transition-all duration-200 hover:from-violet-500 hover:to-fuchsia-500 hover:shadow-violet-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 active:scale-95";

const PayPage: FC<PayPageProps> = ({ className = "" }) => {
  const location = useLocation();
  const { sendPaymentResponse } = useReservationStore();

  useEffect(() => {
    const captureAndSendPaymentResponse = () => {
      const searchParams = new URLSearchParams(location.search);
      const Ds_SignatureVersion = searchParams.get("Ds_SignatureVersion");
      const Ds_MerchantParameters = searchParams.get("Ds_MerchantParameters");
      const Ds_Signature = searchParams.get("Ds_Signature");
      console.log("Payment response:", { Ds_SignatureVersion, Ds_MerchantParameters, Ds_Signature });
      if (Ds_SignatureVersion && Ds_MerchantParameters && Ds_Signature) {
        sendPaymentResponse({ Ds_SignatureVersion, Ds_MerchantParameters, Ds_Signature });
        // GA4: reserva pagada (conversión). Se llega a esta página solo si el banco autorizó.
        // transaction_id evita que GA cuente duplicados si el componente se re-renderiza.
        try {
          const p = JSON.parse(atob(Ds_MerchantParameters));
          trackEvent("purchase", {
            transaction_id: p.Ds_Order,
            value: (parseInt(p.Ds_Amount, 10) || 0) / 100,
            currency: "EUR",
          });
        } catch (e) {
          /* si no se puede decodificar, no bloquear la confirmación */
        }
      }
    };
    captureAndSendPaymentResponse();
  }, [location.search, sendPaymentResponse]);

  return (
    <div className={`nc-PayPage relative min-h-screen overflow-hidden bg-gradient-to-b from-[#0c0a16] to-[#15101f] px-4 py-20 ${className}`} data-nc-id="PayPage">
      <Seo path="/pay-done" title="Reserva confirmada | Karaoke Rooms Barcelona" description="Tu reserva de sala de karaoke en Barcelona se ha confirmado." noindex />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-violet-600/20 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-lg">
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 text-center shadow-2xl backdrop-blur-xl sm:p-10">
          <span className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-emerald-500/15 text-emerald-300">
            <CheckCircle2 size={36} />
          </span>
          <h1 className="text-2xl font-bold text-white sm:text-3xl">¡Gracias por tu reserva! 🎉</h1>
          <p className="mt-3 text-slate-400">Hemos recibido tu pago correctamente. ¡Te esperamos para cantar! 🎤</p>

          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left">
            <p className="flex items-start gap-3 text-sm text-slate-300">
              <Mail size={18} className="mt-0.5 shrink-0 text-violet-300" />
              <span>
                En breve recibirás un <strong className="text-white">email con la confirmación</strong> de tu
                reserva. Revisa también la carpeta de <span className="text-slate-200">spam</span> o promociones.
              </span>
            </p>

            <div className="mt-4 border-t border-white/10 pt-4">
              <p className="text-sm text-slate-400">
                ¿No te llega el email? Escríbenos por WhatsApp al{" "}
                <span className="font-semibold text-white">{WHATSAPP_PHONE_DISPLAY}</span> y lo comprobamos:
              </p>
              <div className="mt-3 flex justify-center sm:justify-start">
                <WhatsAppButton text="Hola, he realizado una reserva en Karaoke Rooms Barcelona y no he recibido el email de confirmación." />
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <Link to="/rooms" className={btnGradient}>Reservar otra sala</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayPage;
